import React, { Component } from 'react';
import SashiDo from 'lib/SashiDo';
import history from 'dashboard/history';
import AppsManager from 'lib/AppsManager';
import Collaborators from 'dashboard/Settings/Collaborators.react';
import DashboardView from 'dashboard/DashboardView.react';
import Field from 'components/Field/Field.react';
import Fieldset from 'components/Fieldset/Fieldset.react';
import FlowView from 'components/FlowView/FlowView.react';
import FormButton from 'components/FormButton/FormButton.react';
import FormModal from 'components/FormModal/FormModal.react';
import joinWithFinal from 'lib/joinWithFinal';
import Notification from 'components/Notification/Notification.react';
import UploadAvatarOrIcon from 'components/UploadAvatarOrIcon/UploadAvatarOrIcon.react';
import Label from 'components/Label/Label.react';
import Modal from 'components/Modal/Modal.react';
import pluck from 'lib/pluck';
import renderFlowFooterChanges from 'lib/renderFlowFooterChanges';
import setDifference from 'lib/setDifference';
import styles from 'dashboard/Settings/Settings.scss';
import TextInput from 'components/TextInput/TextInput.react';
import Toolbar from 'components/Toolbar/Toolbar.react';
import unique from 'lib/unique';
import { dateStringUTC } from 'lib/DateUtils';

const AppInformationFields = ({
  appName,
  appIcon,
  changeAppIcon,
  createdAt,
  ownerEmail,
  viewerEmail,
}) => (<Fieldset
  legend="App Information"
  description="Update general information about your app.">
  <div className={styles.settingsBox}>
    <div className={styles.infoBox} style={{ alignItems: 'center' }}>
      <UploadAvatarOrIcon image={appIcon} onChange={changeAppIcon} appIconMode />

      <div className={styles.info} style={{ paddingLeft: 30 }}>
          <span className={styles.name}>{appName}</span>
          <span className={styles.moreInfo}>
            Was created at <span>{dateStringUTC(createdAt, true)}</span>
          </span>
          <span className={styles.moreInfo}>
            App owner is <span>{ownerEmail}</span>
          </span>
        </div>
    </div>
  </div>
       </Fieldset>);

const CollaboratorsFields = ({
  isCollaborator,
  collaborators,
  ownerEmail,
  viewerEmail,
  addCollaborator,
  removeCollaborator,
}) => {
  return (
    !isCollaborator
      ?
        <Collaborators
          collaborators={collaborators}
          ownerEmail={ownerEmail}
          viewerEmail={viewerEmail}
          onAdd={addCollaborator}
          onRemove={removeCollaborator} />
      : null
  );
};

const ManageAppFields = ({
  isCollaborator,
  hasCollaborators,
  appSlug,
  deleteApp,
}) => {
  return (
    !isCollaborator
      ?
        <Fieldset
          legend="App Management"
          description="These options will affect your entire app.">
          <div className={styles.settingsBox}>
            <Field
              label={
                <Label
                  text="Delete app"
                  description={<span>Completely remove any trace <br />of this app's existence.</span>} />
              }
              input={
                <FormButton
                  color="red"
                  value="Delete this app"
                  onClick={deleteApp} />
              } />
          </div>
        </Fieldset>
      : null
  );
};


export default class GeneralSettings extends DashboardView {
  constructor(props) {
    super();
    this.section = 'App Settings';
    this.subsection = 'General';

    this.state = {
      password: '',
      appName: '',

      showDeleteAppModal: false,

      removedCollaborators: [],
      newCollaborator: '',
    };
  }

  renderContent() {
    if (!this.props.initialFields) {
      return <Toolbar section={this.section} subsection={this.subsection} />;
    }

    const passwordField = (
      <Field
        labelWidth={60}
        label={<Label
          text="Your password"
          description={'We want to make sure it\u2019s really you.'} />
        }
        input={<TextInput
          hidden
          value={this.state.password}
          placeholder="Password"
          onChange={(newValue) => {
            this.setState({ password: newValue });
          }} />} />
    );

    const deleteAppModal = (<FormModal
      title="Delete App"
      subtitle="This is an irreversible action!"
      type={Modal.Types.DANGER}
      open={this.state.showDeleteAppModal}
      submitText="Permanently delete this app"
      inProgressText={'Deleting\u2026'}
      enabled={this.state.password.length > 0 && this.state.appName.toLocaleLowerCase() == this.context.currentApp.name.toLocaleLowerCase()}
      onSubmit={() => AppsManager.deleteApp(this.state.password, this.state.appName, this.context.currentApp.id)}
      onSuccess={(result) => {
        history.push('/products/apps');

        SashiDo.activityTrack({
          type: 'delete_app',
          data: {
            message: `Successfully deleted application ${this.context.currentApp.name}`,
          },
        });

        Intercom('trackEvent', 'app_deleted', { appName: this.context.currentApp.name });
      }}
      onClose={() => this.setState({ showDeleteAppModal: false })}
      clearFields={() => this.setState({ password: '', appName: '' })}>
      <Field
        labelWidth={60}
        label={<Label
          text="Please type in the name of the App to confirm." />
        }
        input={<TextInput
          value={this.state.appName}
          placeholder="App Name"
          onChange={(newValue) => {
            this.setState({ appName: newValue });
          }} />} />
      {passwordField}
                            </FormModal>);

    const initialFields = {
      requestLimit: this.props.initialFields.pricing_plan.request_limit,
      appName: this.context.currentApp.name,
      createdAt: this.context.currentApp.createdAt,
      appIcon: this.context.currentApp.icon,
      collaborators: this.props.initialFields.collaborators,
    };

    const collaboratorRemovedWarningModal = this.state.removedCollaborators.length > 0 ? (<Modal
      title="Check Master Key Access"
      icon="keys-solid"
      type={Modal.Types.DANGER}
      showCancel={false}
      confirmText="Got it!"
      onConfirm={() => this.setState({ removedCollaborators: [] })}
      buttonsInCenter
      textModal>
      <span>We have removed <strong>{joinWithFinal('', this.state.removedCollaborators.map(c => c.userName || c.username), ', ', ' and ')}</strong> from this app. If they had saved the master key, they may still have access via an SDK or the API. To be sure, you can reset your master key in the Keys section of app settings.</span>
                                                                                          </Modal>) : null;

    const setCollaborators = (setField, unused, allCollabs) => {
      const addedCollaborators = setDifference(allCollabs, initialFields.collaborators, compareCollaborators);
      const removedCollaborators = setDifference(initialFields.collaborators, allCollabs, compareCollaborators);
      if (addedCollaborators.length === 0 && removedCollaborators.length === 0) {
        // This is neccessary because the footer computes whether or not show a change by reference equality.
        allCollabs = initialFields.collaborators;
      }
      setField('collaborators', allCollabs);
    };

    return (
      <div>
        <Notification note={this.state.newCollaborator} close />
        <FlowView
          initialFields={initialFields}
          footerContents={({ changes }) => renderFlowFooterChanges(changes, initialFields, generalFieldsOptions)}
          onSubmit={({ changes, fields, setField }) => {
            const promiseList = [];

            if (changes.appIcon !== undefined) {
              promiseList.push(AppsManager.changeAppIcon({ id: this.context.currentApp.id, icon: changes.appIcon }));
            }

            if (changes.collaborators !== undefined) {
              let p_collaborators = Promise.resolve();

              // TODO: @pivanov - something is wrong here we need to make it clear

              const addedCollaborators = setDifference(changes.collaborators, initialFields.collaborators, compareCollaborators);

              addedCollaborators.forEach(({ username }) => {
                p_collaborators = p_collaborators.then(() => (new Promise((resolve) => {
                  SashiDo.createCollaborator({ username: username.toLowerCase(), app_name: this.context.currentApp.name, app_slug: this.context.currentApp.slug }).then(() => {
                    Intercom('trackEvent', 'invite_collaborator', { name: username.toLowerCase() });

                    SashiDo.activityTrack({
                      type: 'add_collaborator',
                      data: {
                        message: `Collaborator ${username.toLowerCase()} was added to app ${this.context.currentApp.name}`,
                      },
                    });

                    this.setState({ newCollaborator: { type: 'info', message: `${username.toLowerCase()} was successfully invited to join as collaborator.` } });
                    resolve();
                  }, resolve);
                })).then(() => this.context.currentApp.addCollaborator(username.toLowerCase())));
              });

              const removedCollaborators = setDifference(initialFields.collaborators, changes.collaborators, compareCollaborators);
              removedCollaborators.forEach((data) => {
                SashiDo.activityTrack({
                  type: 'delete_collaborator',
                  data: {
                    message: `Collaborator ${data.username.toLowerCase()} was removed from app ${this.context.currentApp.name}`,
                  },
                });

                p_collaborators = SashiDo.removeCollaborator({ email: data.username, appId: this.context.currentApp.id });
              });

              promiseList.push(p_collaborators);
            }

            return Promise.all(promiseList).catch((errors) => {
              Promise.reject({ error: unique(pluck(errors, 'error')).join(' ') });
            });
          }}
          renderForm={({
 changes, fields, setField, resetFields,
}) => {
            const isCollaborator = this.state.currentUser.id !== this.context.currentApp.user.objectId;
            const ownerEmail = (fields.collaborators.find(r => r.objectId === this.context.currentApp.user.objectId) || {}).username;


            return (
              <div className={[styles.settings_page, 'ShowFooter'].join(' ')}>
                <div className={styles.holder}>
                  <AppInformationFields
                    appName={fields.appName}
                    appIcon={fields.appIcon}
                    changeAppIcon={setField.bind(this, 'appIcon')}
                    createdAt={fields.createdAt}
                    ownerEmail={ownerEmail}
                    viewerEmail={this.state.currentUser.email} />

                  <CollaboratorsFields
                    isCollaborator={isCollaborator}
                    collaborators={fields.collaborators}
                    ownerEmail={ownerEmail}
                    viewerEmail={this.state.currentUser.email}
                    addCollaborator={setCollaborators.bind(undefined, setField)}
                    removeCollaborator={setCollaborators.bind(undefined, setField)} />

                  {
                    <ManageAppFields
                      isCollaborator={isCollaborator}
                      hasCollaborators={initialFields.collaborators.length > 0}
                      appSlug={this.context.currentApp.slug}
                      deleteApp={() => this.setState({ showDeleteAppModal: true })} />
                  }
                </div>
              </div>
            );
          }} />
        {deleteAppModal}
        {collaboratorRemovedWarningModal}

        <Toolbar section={this.section} subsection={this.subsection} />
      </div>
    );
  }
}

let compareCollaborators = (collab1, collab2) => collab1.username === collab2.username;

let generalFieldsOptions = {
  appName: {
    friendlyName: 'app name',
    showTo: true,
  },
  collaborators: {
    friendlyName: 'collaborator',
    friendlyNamePlural: 'collaborators',
    type: 'set',
    equalityPredicate: compareCollaborators,
  },
};
